import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { Observable, BehaviorSubject, Subject, interval, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, startWith, takeUntil, filter } from 'rxjs/operators';
import { PreAlertService } from '../../services/pre-alert.service';
import { AuthService } from '../../services/auth.service';
import { SignalRService, ScanningProgressUpdate, ScanActivityBroadcast } from '../../services/signalr.service';
import {
  PreAlert,
  PaginatedResponse,
  PreAlertSearchFilter
} from '../../../models/pre-alert.models';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmInput } from '@spartan-ng/helm/input';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideDownload, lucideUpload, lucideRefreshCw, lucideSearch, lucideX,
  lucideArrowUpDown, lucideEye, lucideTrash2, lucideChevronLeft, lucideChevronRight,
  lucideChevronDown, lucideChevronsLeft, lucideChevronsRight,
  lucideSlidersHorizontal, lucideInbox
} from '@ng-icons/lucide';
import { HlmIcon } from '@spartan-ng/helm/icon';
import {
  PageHeaderComponent, StatusBadgeComponent, StatusBadgeVariant,
  InlineAlertComponent, EmptyStateComponent, SkeletonComponent
} from '@shared/ui/components';

@Component({
  selector: 'app-pre-alert-list',
  imports: [
    CommonModule, FormsModule, RouterModule,
    ...HlmCardImports, HlmButton, HlmInput,
    NgIcon, HlmIcon,
    PageHeaderComponent, StatusBadgeComponent, InlineAlertComponent,
    EmptyStateComponent, SkeletonComponent,
  ],
  providers: [provideIcons({
    lucideDownload, lucideUpload, lucideRefreshCw, lucideSearch, lucideX,
    lucideArrowUpDown, lucideEye, lucideTrash2, lucideChevronLeft, lucideChevronRight,
    lucideChevronDown, lucideChevronsLeft, lucideChevronsRight,
    lucideSlidersHorizontal, lucideInbox
  })],
  templateUrl: './pre-alert-list.html',
  styleUrl: './pre-alert-list.css'
})
export class PreAlertList implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  preAlerts: PreAlert[] = [];
  isLoading = false;
  error: string | null = null;
  
  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalCount = 0;
  totalPages = 0;
  
  // Search and filtering
  searchTerm = '';
  private searchSubject = new BehaviorSubject<string>('');
  
  // Filter state
  filter: PreAlertSearchFilter = {
    page: 1,
    page_size: 20,
    sort_by: 'eta',
    sort_order: 'desc'
  };
  
  // Filter form fields
  filterForm = {
    lading_bill: '',
    transport_company: '',
    vessel_no: '',
    arrival_location: '',
    departure_location: '',
    status: '',
    eta_from: '',
    eta_to: ''
  };
  
  showAdvancedFilters = false;

  // === SIGNALR REAL-TIME STATE ===

  // Real-time scanning progress by pre-alert
  scanningProgressMap: Map<string, ScanningProgressUpdate> = new Map();

  // Recent scanning activity
  realtimeScanActivity: ScanActivityBroadcast[] = [];

  // Pre-alert scanning metrics
  preAlertMetrics: Map<string, {
    totalCartons: number;
    scannedCartons: number;
    completionPercentage: number;
    scanningVelocity: number;
    estimatedCompletion?: Date;
    activeScanner?: string;
    lastActivity: Date;
  }> = new Map();

  // Live scanning indicators
  activeScanningPreAlerts: Set<string> = new Set();
  scanningActivityFeed: { preAlertId: string; activity: string; timestamp: Date; user: string }[] = [];

  // Real-time UI controls
  showRealtimeProgress = true;
  showScanningActivity = true;
  enableLiveUpdates = true;
  liveUpdateInterval = 15000; // 15 seconds

  // SignalR connection status
  signalRConnected = false;
  lastScanningUpdate: Date | null = null;

  // Role-based access control
  isShopeeChecker$: Observable<boolean>;

  constructor(
    private preAlertService: PreAlertService,
    private authService: AuthService,
    private signalRService: SignalRService,
    private router: Router
  ) {
    this.isShopeeChecker$ = this.authService.isShopeeChecker$;
  }

  ngOnInit(): void {
    this.loadPreAlerts();
    this.setupSearch();
    this.initializeSignalRSubscriptions();
    this.setupLiveUpdates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupSignalRSubscriptions();
  }

  private setupSearch(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(searchTerm => {
        if (searchTerm.trim()) {
          this.isLoading = true;
          return this.preAlertService.searchPreAlerts(searchTerm);
        } else {
          return this.loadPreAlertsData();
        }
      })
    ).subscribe({
      next: (response) => {
        this.handlePreAlertResponse(response);
        this.isLoading = false;
      },
      error: (error) => {
        this.handleError('Failed to search pre-alerts', error);
        this.isLoading = false;
      }
    });
  }

  private loadPreAlertsData(): Observable<PaginatedResponse<PreAlert>> {
    this.isLoading = true;
    return this.preAlertService.getPreAlerts(this.filter);
  }

  loadPreAlerts(): void {
    this.loadPreAlertsData().subscribe({
      next: (response) => {
        this.handlePreAlertResponse(response);
        this.isLoading = false;
      },
      error: (error) => {
        this.handleError('Failed to load pre-alerts', error);
        this.isLoading = false;
      }
    });
  }

  private handlePreAlertResponse(response: PaginatedResponse<PreAlert>): void {
    if (response && response.items) {
      this.preAlerts = response.items;
      this.totalCount = response.totalCount;
      this.currentPage = response.pageNumber;
      this.pageSize = response.pageSize;
      this.totalPages = response.totalPages;
      this.error = null;
    } else {
      this.error = 'Failed to load pre-alerts';
      this.preAlerts = [];
    }
  }

  private handleError(message: string, error: any): void {
    console.error(message, error);
    this.error = message;
    this.preAlerts = [];
  }

  onSearch(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onPageChange(page: number): void {
    this.filter.page = page;
    this.currentPage = page;
    this.loadPreAlerts();
  }

  onPageSizeChange(size: number): void {
    this.filter.page_size = size;
    this.filter.page = 1;
    this.pageSize = size;
    this.currentPage = 1;
    this.loadPreAlerts();
  }

  onSort(column: string): void {
    if (this.filter.sort_by === column) {
      this.filter.sort_order = this.filter.sort_order === 'asc' ? 'desc' : 'asc';
    } else {
      this.filter.sort_by = column as any;
      this.filter.sort_order = 'desc';
    }
    this.filter.page = 1;
    this.currentPage = 1;
    this.loadPreAlerts();
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  applyFilters(): void {
    // Convert form values to filter object
    this.filter = {
      ...this.filter,
      page: 1,
      lading_bill: this.filterForm.lading_bill || undefined,
      transport_company: this.filterForm.transport_company || undefined,
      vessel_no: this.filterForm.vessel_no || undefined,
      arrival_location: this.filterForm.arrival_location || undefined,
      departure_location: this.filterForm.departure_location || undefined,
      status: this.filterForm.status || undefined,
      eta_from: this.filterForm.eta_from ? new Date(this.filterForm.eta_from) : undefined,
      eta_to: this.filterForm.eta_to ? new Date(this.filterForm.eta_to) : undefined
    };
    
    this.currentPage = 1;
    this.loadPreAlerts();
  }

  clearFilters(): void {
    this.filterForm = {
      lading_bill: '',
      transport_company: '',
      vessel_no: '',
      arrival_location: '',
      departure_location: '',
      status: '',
      eta_from: '',
      eta_to: ''
    };
    
    this.filter = {
      page: 1,
      page_size: this.pageSize,
      sort_by: 'eta',
      sort_order: 'desc'
    };
    
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadPreAlerts();
  }

  refresh(): void {
    this.loadPreAlerts();
  }

  exportData(): void {
    this.isLoading = true;
    this.preAlertService.exportPreAlerts(this.filter).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `pre-alerts-${new Date().toISOString().split('T')[0]}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isLoading = false;
      },
      error: (error) => {
        this.handleError('Failed to export data', error);
        this.isLoading = false;
      }
    });
  }

  // Helper methods for template
  formatDate(date: Date | string): string {
    return this.preAlertService.formatDate(date);
  }

  formatWeight(weight: number): string {
    return this.preAlertService.formatWeight(weight);
  }

  getDaysUntilETA(eta: Date | string): number {
    return this.preAlertService.getDaysUntilETA(eta);
  }

  getETAStatus(eta: Date | string): string {
    return this.preAlertService.getETAStatus(eta);
  }

  getETAStatusClass(eta: Date | string): string {
    const status = this.getETAStatus(eta);
    switch (status) {
      case 'overdue': return 'text-danger';
      case 'today': return 'text-warning';
      case 'upcoming': return 'text-info';
      default: return 'text-success';
    }
  }

  getETAStatusBadgeClass(eta: Date | string): string {
    const status = this.getETAStatus(eta);
    switch (status) {
      case 'overdue': return 'danger';
      case 'today': return 'warning';
      case 'upcoming': return 'info';
      default: return 'success';
    }
  }

  getStatusBadgeClass(status: string): string {
    return this.preAlertService.getStatusBadgeClass(status);
  }

  getPageArray(): number[] {
    const pages: number[] = [];
    const start = Math.max(1, this.currentPage - 2);
    const end = Math.min(this.totalPages, this.currentPage + 2);
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  // Excel Import functionality
  openExcelImport(): void {
    try {
      // Navigate to the Shopee Excel Upload component
      console.log('Navigating to Excel upload...');
      this.router.navigate(['/dashboard/shopee-excel-upload']).then(success => {
        if (success) {
          console.log('Navigation successful');
        } else {
          console.error('Navigation failed');
          this.error = 'Unable to access Excel import feature. Please check your permissions.';
        }
      }).catch(error => {
        console.error('Navigation error:', error);
        this.error = 'Error navigating to Excel import: ' + error.message;
      });
    } catch (error) {
      console.error('Excel import navigation error:', error);
      this.error = 'Unable to access Excel import feature';
    }
  }

  /**
   * Delete pre-alert and all associated data
   */
  deletePreAlert(preAlert: PreAlert): void {
    const confirmMessage = `Are you sure you want to delete pre-alert "${preAlert.ladingBill}"? This will permanently delete:\n\n• The pre-alert record\n• All associated Shopee orders\n• All related cartons and parcels\n\nThis action cannot be undone.`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    this.preAlertService.deletePreAlert(preAlert.ladingBill).subscribe({
      next: (response) => {
        if (response.retcode === 0) {
          // Remove from local array
          this.preAlerts = this.preAlerts.filter(pa => pa.ladingBill !== preAlert.ladingBill);
          this.totalCount--;
          
          // Show success message
          alert(`Pre-alert "${preAlert.ladingBill}" and all associated data deleted successfully.`);
          
          // Reload current page if we removed the last item on this page
          if (this.preAlerts.length === 0 && this.currentPage > 1) {
            this.currentPage--;
            this.loadPreAlerts();
          }
        } else {
          this.error = response.message || 'Failed to delete pre-alert';
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.handleError('Failed to delete pre-alert', error);
        this.isLoading = false;
      }
    });
  }

  // === DS TOKEN HELPERS ===

  getEtaVariant(eta: Date | string): StatusBadgeVariant {
    const status = this.getETAStatus(eta);
    switch (status) {
      case 'overdue': return 'danger';
      case 'today':   return 'warning';
      case 'upcoming': return 'info';
      default:        return 'neutral';
    }
  }

  getEtaLabel(eta: Date | string): string {
    const days = this.getDaysUntilETA(eta);
    const status = this.getETAStatus(eta);
    if (status === 'overdue') return `Overdue ${Math.abs(days)}d`;
    if (days === 0)  return 'Today';
    if (days === 1)  return 'Tomorrow';
    return `+${days}d`;
  }

  getPreAlertStatusVariant(status: string): StatusBadgeVariant {
    switch (status) {
      case 'Pending':   return 'neutral';
      case 'InTransit': return 'info';
      case 'Arrived':   return 'warning';
      case 'Cleared':   return 'default';
      case 'Delivered': return 'success';
      default:          return 'neutral';
    }
  }

  // Template helper methods
  trackByPreAlert(index: number, preAlert: PreAlert): string {
    return preAlert.ladingBill;
  }

  get Math() {
    return Math;
  }

  // === SIGNALR REAL-TIME INTEGRATION METHODS ===

  /**
   * Initialize SignalR subscriptions for real-time scanning progress
   */
  private initializeSignalRSubscriptions(): void {
    // Monitor SignalR connection status
    this.signalRService.connectionStatus$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(status => {
      this.signalRConnected = status.isConnected;
    });

    // Subscribe to all scanning activity for pre-alert awareness
    this.signalRService.subscribeToAllScanningEvents().pipe(
      takeUntil(this.destroy$)
    ).subscribe(scanActivity => {
      this.handleRealtimeScanActivity(scanActivity);
    });

    // Subscribe to scanning progress updates for currently visible pre-alerts
    this.preAlerts.forEach(preAlert => {
      this.subscribeToPreAlertProgress(preAlert.ladingBill);
    });
  }

  /**
   * Subscribe to scanning progress for a specific pre-alert
   */
  private subscribeToPreAlertProgress(preAlertId: string): void {
    this.signalRService.subscribeToScanningProgress(preAlertId).pipe(
      takeUntil(this.destroy$)
    ).subscribe(progressUpdate => {
      this.handleRealtimeScanningProgress(progressUpdate);
    });
  }

  /**
   * Handle real-time scanning activity
   */
  private handleRealtimeScanActivity(scanActivity: ScanActivityBroadcast): void {
    this.lastScanningUpdate = new Date();

    // Add to recent activity
    this.realtimeScanActivity.unshift(scanActivity);
    if (this.realtimeScanActivity.length > 30) {
      this.realtimeScanActivity = this.realtimeScanActivity.slice(0, 30);
    }

    // Track active scanning pre-alerts
    if (scanActivity.preAlertId) {
      this.activeScanningPreAlerts.add(scanActivity.preAlertId);

      // Add to activity feed
      this.addToScanningActivityFeed(
        scanActivity.preAlertId,
        `${scanActivity.scannerUserName} scanned ${scanActivity.barcode}`,
        scanActivity.scannerUserName
      );

      // Remove from active scanning after inactivity
      setTimeout(() => {
        const hasRecentActivity = this.realtimeScanActivity
          .filter(activity => activity.preAlertId === scanActivity.preAlertId)
          .some(activity => (new Date().getTime() - activity.scanTime.getTime()) < 60000);

        if (!hasRecentActivity) {
          this.activeScanningPreAlerts.delete(scanActivity.preAlertId);
        }
      }, 60000);
    }

    // Show notification for scanning activity if enabled
    if (this.showScanningActivity && scanActivity.success) {
      this.showRealtimeNotification(
        `Scanning activity: ${scanActivity.scannerUserName} scanned carton for ${scanActivity.preAlertId}`,
        'info'
      );
    }
  }

  /**
   * Handle real-time scanning progress updates
   */
  private handleRealtimeScanningProgress(progressUpdate: ScanningProgressUpdate): void {
    this.lastScanningUpdate = new Date();

    // Store the progress update
    this.scanningProgressMap.set(progressUpdate.preAlertId, progressUpdate);

    // Update metrics
    this.preAlertMetrics.set(progressUpdate.preAlertId, {
      totalCartons: progressUpdate.totalCartons,
      scannedCartons: progressUpdate.scannedCartons,
      completionPercentage: progressUpdate.completionPercentage,
      scanningVelocity: progressUpdate.scanningVelocity,
      estimatedCompletion: progressUpdate.estimatedCompletion,
      activeScanner: progressUpdate.activeScanner,
      lastActivity: progressUpdate.lastScanTime
    });

    // Update pre-alert in the list if visible
    const preAlert = this.preAlerts.find(pa => pa.ladingBill === progressUpdate.preAlertId);
    if (preAlert) {
      // Add real-time scanning data to pre-alert for display
      (preAlert as any).realtimeScanProgress = this.preAlertMetrics.get(progressUpdate.preAlertId);

      // Highlight recently updated pre-alert
      this.highlightPreAlert(progressUpdate.preAlertId);
    }

    // Show progress notification if enabled
    if (this.showRealtimeProgress) {
      this.showRealtimeNotification(
        `${progressUpdate.preAlertId}: ${progressUpdate.completionPercentage}% complete (${progressUpdate.scannedCartons}/${progressUpdate.totalCartons})`,
        'success'
      );
    }
  }

  /**
   * Add activity to the scanning feed
   */
  private addToScanningActivityFeed(preAlertId: string, activity: string, user: string): void {
    this.scanningActivityFeed.unshift({
      preAlertId,
      activity,
      timestamp: new Date(),
      user
    });

    // Limit activity feed size
    if (this.scanningActivityFeed.length > 15) {
      this.scanningActivityFeed = this.scanningActivityFeed.slice(0, 15);
    }
  }

  /**
   * Setup live updates for scanning metrics
   */
  private setupLiveUpdates(): void {
    if (!this.enableLiveUpdates) return;

    interval(this.liveUpdateInterval).pipe(
      takeUntil(this.destroy$),
      filter(() => this.enableLiveUpdates && this.signalRConnected)
    ).subscribe(() => {
      // Refresh scanning metrics for visible pre-alerts
      this.refreshScanningMetrics();
    });
  }

  /**
   * Refresh scanning metrics from the server
   */
  private refreshScanningMetrics(): void {
    if (this.preAlerts.length === 0) return;

    // Get scanning metrics for all visible pre-alerts
    this.preAlerts.forEach(preAlert => {
      // Could call a service method here to get updated metrics
      // For now, we rely on SignalR real-time updates
    });
  }

  /**
   * Highlight a pre-alert briefly
   */
  private highlightPreAlert(preAlertId: string): void {
    // Add CSS class for highlighting
    const element = document.querySelector(`[data-pre-alert-id="${preAlertId}"]`);
    if (element) {
      element.classList.add('highlight-update');
      setTimeout(() => {
        element.classList.remove('highlight-update');
      }, 2000);
    }
  }

  /**
   * Show real-time notification
   */
  private showRealtimeNotification(message: string, type: 'success' | 'info' | 'warning' = 'info'): void {
    // Simple console notification for now - could be enhanced with toast notifications
    console.log(`🔴 REAL-TIME: ${message}`);

    // Could integrate with a notification service here
    // this.notificationService.show(message, type);
  }

  /**
   * Cleanup SignalR subscriptions
   */
  private async cleanupSignalRSubscriptions(): Promise<void> {
    try {
      // Unsubscribe from all pre-alert progress updates
      const preAlertIds = this.preAlerts.map(pa => pa.ladingBill);
      for (const preAlertId of preAlertIds) {
        await this.signalRService.unsubscribeFromScanningEvents(preAlertId);
      }
    } catch (error) {
      console.warn('Error cleaning up SignalR subscriptions:', error);
    }
  }

  // === SIGNALR UI GETTERS ===

  get hasRealtimeScanningUpdates(): boolean {
    return this.scanningProgressMap.size > 0 || this.realtimeScanActivity.length > 0;
  }

  get activeScanningCount(): number {
    return this.activeScanningPreAlerts.size;
  }

  get recentScanningActivityFeed(): { preAlertId: string; activity: string; timestamp: Date; user: string }[] {
    return this.scanningActivityFeed.slice(0, 5);
  }

  get scanningProgressSummary(): {
    totalPreAlerts: number;
    preAlertsWithProgress: number;
    averageCompletion: number;
    activeScanning: number;
  } {
    const totalPreAlerts = this.preAlerts.length;
    const preAlertsWithProgress = this.scanningProgressMap.size;
    const activeScanning = this.activeScanningPreAlerts.size;

    let totalCompletion = 0;
    this.scanningProgressMap.forEach(progress => {
      totalCompletion += progress.completionPercentage;
    });

    const averageCompletion = preAlertsWithProgress > 0 ? totalCompletion / preAlertsWithProgress : 0;

    return {
      totalPreAlerts,
      preAlertsWithProgress,
      averageCompletion: Math.round(averageCompletion),
      activeScanning
    };
  }

  get connectionStatusText(): string {
    return this.signalRConnected ? 'Connected' : 'Disconnected';
  }

  get connectionStatusClass(): string {
    return this.signalRConnected ? 'text-success' : 'text-danger';
  }

  /**
   * Check if a pre-alert has active scanning
   */
  isPreAlertBeingScanned(preAlertId: string): boolean {
    return this.activeScanningPreAlerts.has(preAlertId);
  }

  /**
   * Get scanning progress for a pre-alert
   */
  getPreAlertScanningProgress(preAlertId: string): ScanningProgressUpdate | null {
    return this.scanningProgressMap.get(preAlertId) || null;
  }

  /**
   * Get scanning metrics for a pre-alert
   */
  getPreAlertMetrics(preAlertId: string): {
    totalCartons: number;
    scannedCartons: number;
    completionPercentage: number;
    scanningVelocity: number;
    estimatedCompletion?: Date;
    activeScanner?: string;
    lastActivity: Date;
  } | null {
    return this.preAlertMetrics.get(preAlertId) || null;
  }

  /**
   * Get time since last scanning update
   */
  getTimeSinceLastScanningUpdate(): string {
    if (!this.lastScanningUpdate) return 'Never';

    const now = new Date();
    const diff = now.getTime() - this.lastScanningUpdate.getTime();
    const seconds = Math.floor(diff / 1000);

    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  }

  /**
   * Get completion status class for progress bars
   */
  getCompletionStatusClass(completionPercentage: number): string {
    if (completionPercentage >= 100) return 'bg-success';
    if (completionPercentage >= 75) return 'bg-info';
    if (completionPercentage >= 50) return 'bg-warning';
    return 'bg-danger';
  }

  /**
   * Get scanning velocity display
   */
  getScanningVelocityDisplay(velocity: number): string {
    if (velocity === 0) return 'N/A';
    if (velocity < 1) return `${(velocity * 60).toFixed(1)}/min`;
    return `${velocity.toFixed(1)}/sec`;
  }

  // === SIGNALR UI ACTIONS ===

  toggleRealtimeProgress(): void {
    this.showRealtimeProgress = !this.showRealtimeProgress;
  }

  toggleScanningActivity(): void {
    this.showScanningActivity = !this.showScanningActivity;
  }

  toggleLiveUpdates(): void {
    this.enableLiveUpdates = !this.enableLiveUpdates;

    if (this.enableLiveUpdates) {
      this.setupLiveUpdates();
    }
  }

  clearRealtimeScanningData(): void {
    this.scanningProgressMap.clear();
    this.realtimeScanActivity = [];
    this.preAlertMetrics.clear();
    this.activeScanningPreAlerts.clear();
    this.scanningActivityFeed = [];
    this.lastScanningUpdate = null;
  }

  forceRefreshScanningMetrics(): void {
    this.refreshScanningMetrics();
  }

  forceSignalRReconnect(): void {
    this.signalRService.forceReconnect();
  }

  /**
   * Navigate to mobile scanner for specific pre-alert
   */
  openMobileScanner(preAlertId: string): void {
    this.router.navigate(['/dashboard/mobile-scanner'], {
      queryParams: { preAlertId }
    });
  }

  /**
   * Show detailed scanning progress for pre-alert
   */
  showScanningDetails(preAlertId: string): void {
    const progress = this.getPreAlertScanningProgress(preAlertId);
    const metrics = this.getPreAlertMetrics(preAlertId);

    if (progress && metrics) {
      const message = `
Scanning Progress for ${preAlertId}:
• Completion: ${progress.completionPercentage}%
• Scanned: ${progress.scannedCartons}/${progress.totalCartons}
• Velocity: ${this.getScanningVelocityDisplay(progress.scanningVelocity)}
• Active Scanner: ${progress.activeScanner || 'None'}
• Last Activity: ${progress.lastScanTime.toLocaleString()}
      `;

      alert(message);
    } else {
      alert(`No scanning data available for ${preAlertId}`);
    }
  }
}




